import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { object, string, email, type infer as Infer } from "zod";
import { useStore } from "./auth-store";
import { useAuth } from "contexts/AuthProvider";
import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import { createLogger } from "debug/DebugingLogger";
const log = createLogger("LoginPage");
import { Button } from "components/common/Button";
import { Tooltip } from "@/components/common/Tooltip";
import { AuthHeader } from "@/components/authentication/common/AuthHeader";
import { AuthStatusBanner } from "@/components/authentication/common/AuthStatusBanner";
import { GoogleSignInButton } from "@/components/authentication/common/GoogleSignInButton";
const loginSchema = object({
  email: email("Email Address is invalid").min(1, "Email address is required"),
  password: string().min(1, "Password is required"),
});

export type LoginInput = Infer<typeof loginSchema>;

export const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const store = useStore();
  // OAuth catalog flow: if we arrived here via /oauth/authorize's
  // redirect, the destination is encoded as a `return_to` URL param.
  // In dev the target sits on the backend (`localhost:8081/authorize`),
  // not the React app — so we pass the absolute URL through Google
  // OAuth `state.from`. The backend's post-login redirect uses
  // absolute URLs verbatim and prepends the React origin to relative
  // paths. We still validate the URL parses to close the
  // open-redirect surface from a malformed `return_to`.
  const returnToParam = new URLSearchParams(location.search).get("return_to");
  const oauthFromAbsolute = returnToParam
    ? (() => {
        try {
          // Just validate parseability — pass through the original
          // string so the backend sees the exact URL we received.
          new URL(returnToParam);
          return returnToParam;
        } catch {
          return null;
        }
      })()
    : null;
  const from =
    oauthFromAbsolute ||
    ((location.state as { from?: { pathname?: string } })?.from
      ?.pathname as string) ||
    "/overview";

  const {
    authenticated,
    setAuthenticated,
    setEmail,
    setAccountStatus,
    setTosAccepted,
    setPpAccepted,
  } = useAuth();
  const [errorMessage, setErrorMessage] = useState("");
  const [verificationMessage, setVerificationMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusVariant, setStatusVariant] = useState<"info" | "error">("info");
  // Set only when login is refused for an unverified address — the one refusal the
  // user can fix themselves, so it gets a resend rather than a plain error.
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">(
    "idle",
  );
  const [registrationOpen, setRegistrationOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // OAuth catalog flow — if the user landed here via a redirect from
    // /oauth/authorize, stash the destination so we can navigate back
    // after sign-in (works for both email+password and Google OAuth
    // round-trips). AuthProvider checks the same key on auth-state
    // change for the Google path.
    const returnTo = params.get("return_to");
    if (returnTo) {
      sessionStorage.setItem("oauth_return_to", returnTo);
    }
    if (params.get("verified") === "true") {
      setVerificationMessage(
        "Your email has been successfully verified. Please log in.",
      );
      setTimeout(() => setVerificationMessage(""), 30000);
    }
    if (params.get("error") === "no_account") {
      setStatusMessage(
        "No account found. Sign-up is invite-only right now — ask whoever invited you to send your registration link.",
      );
      setStatusVariant("info");
    }
    if (params.get("error") === "google_email_unverified") {
      setStatusMessage(
        "Google hasn't verified that email address, so we can't sign you in with it. Verify it with Google, or sign in with your password.",
      );
      setStatusVariant("error");
    }
    if (params.get("error") === "login_failed") {
      setStatusMessage(
        "Unable to sign in. Please try again or contact support.",
      );
      setStatusVariant("error");
    }
    if (params.get("error") === "login_cancelled") {
      setStatusMessage("Sign in was cancelled. Please try again.");
      setStatusVariant("info");
    }
    const verifyCode = params.get("verify");
    if (verifyCode) {
      axiosInstance
        .get(`/api/auth/verifyemail/${verifyCode}`)
        .then(() => {
          setVerificationMessage(
            "Your email has been successfully verified. Please log in.",
          );
          setTimeout(() => setVerificationMessage(""), 30000);
          navigate("/", { replace: true });
        })
        .catch(() => {
          setErrorMessage("Verification link is invalid or has expired.");
          setTimeout(() => setErrorMessage(""), 5000);
          navigate("/", { replace: true });
        });
    }
  }, [location.search]);

  useEffect(() => {
    axiosInstance
      .get("/api/auth/registration-status")
      .then((res) => setRegistrationOpen(!!res.data?.registration_open))
      .catch(() => setRegistrationOpen(false));
  }, []);

  // The endpoint answers 202 for every address, so there is nothing to branch on
  // and nothing to report back beyond "sent".
  const resendVerification = async () => {
    setResendState("sending");
    try {
      await axiosInstance.post("/api/auth/resend-verification", {
        email: unverifiedEmail,
      });
    } catch (error) {
      log.error("Error resending verification:%o", error);
    }
    setResendState("sent");
  };

  const loginUser = async (data: LoginInput) => {
    try {
      store.setRequestLoading(true);
      const response = await axiosInstance.post(`/api/auth/login`, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.status === 200) {
        store.setRequestLoading(false);
        log.info("Login successful %o", response.data);
        // Fetch consent state BEFORE setting authenticated to prevent
        // ConsentGate from briefly flashing the consent modal.
        try {
          const authRes = await axiosInstance.get("/api/auth/check");
          setTosAccepted(!!authRes.data.tos_accepted);
          setPpAccepted(!!authRes.data.pp_accepted);
        } catch {
          // ConsentGate will show the modal if consent state is unknown
        }
        setAuthenticated(true);
        setEmail(response.data.email);
        setAccountStatus(response.data.status.toLowerCase());
        // OAuth catalog flow: if a return_to was stashed by the
        // useEffect on mount, send the user back to that absolute URL
        // (typically `<backend>/authorize?...`). Full reload so the
        // session cookie travels with the navigation.
        const returnTo = sessionStorage.getItem("oauth_return_to");
        if (returnTo) {
          sessionStorage.removeItem("oauth_return_to");
          window.location.href = returnTo;
        } else {
          navigate("/overview");
        }
      }
    } catch (error: unknown) {
      log.error("Error during login:%o", error);
      store.setRequestLoading(false);
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.status === "waitlisted") {
          setStatusMessage(error.response.data.message);
          setStatusVariant("info");
          setErrorMessage("");
          return;
        }
        if (error.response?.data?.status === "unverified") {
          setStatusMessage(error.response.data.message);
          setStatusVariant("info");
          setErrorMessage("");
          setUnverifiedEmail(data.email);
          setResendState("idle");
          return;
        }
        const resMessage =
          (error.response?.data?.message as string) ||
          "Incorrect email or password";
        log.error(resMessage);
        setErrorMessage(resMessage);
      } else {
        setErrorMessage("Incorrect email or password");
      }
      setTimeout(() => {
        setErrorMessage("");
      }, 5000);
    }
  };

  const methods = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const {
    reset,
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (store.isSubmitSuccessful) {
      reset();
    }
  }, [store.isSubmitSuccessful, reset]);

  const onSubmitHandler: SubmitHandler<LoginInput> = (values) => {
    setErrorMessage("");
    loginUser(values);
  };

  if (authenticated) {
    return <Navigate to="/overview" />;
  }

  return (
    <section className="auth-page">
      <div className="flex flex-col justify-center items-center min-h-full px-6 py-12 lg:px-8">
        <div className="w-full sm:max-w-sm flex flex-col gap-8">
          <AuthHeader title="Welcome back!" message={verificationMessage} />

          <AuthStatusBanner message={statusMessage} variant={statusVariant} />

          {unverifiedEmail && (
            <div className="flex flex-col items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={resendVerification}
                loading={resendState === "sending"}
                disabled={resendState === "sent"}
              >
                {resendState === "sent"
                  ? "Verification email sent"
                  : "Resend verification email"}
              </Button>
              {resendState === "sent" && (
                <p className="text-center text-sm text-fg-medium">
                  If that address has an account awaiting verification, a new
                  link is on its way.
                </p>
              )}
            </div>
          )}

          <GoogleSignInButton from={from} />

          <div className="auth-divider">
            <hr />
            <span>Or</span>
            <hr />
          </div>

          <form
            noValidate
            onSubmit={handleSubmit(onSubmitHandler)}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-4">
              <Tooltip
                title={errors.email?.message || ""}
                open={!!errors.email}
                placement="bottom"
              >
                <input
                  id="email"
                  type="email"
                  className="auth-input"
                  placeholder="Email address"
                  aria-invalid={!!errors.email}
                  {...register("email")}
                />
              </Tooltip>
              <div className="relative">
                <Tooltip
                  title={errors.password?.message || ""}
                  open={!!errors.password}
                  placement="bottom"
                >
                  <input
                    id="password"
                    type="password"
                    className="auth-input"
                    placeholder="Password"
                    aria-invalid={!!errors.password}
                    {...register("password")}
                    autoComplete="current-password"
                  />
                </Tooltip>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/forgot-password");
                  }}
                  className="flex items-center cursor-pointer hover:underline item-height"
                  style={{ color: "var(--color-fg-muted)", fontSize: "12px" }}
                >
                  Forgot Password ?
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <p
                className="text-error flex items-center item-height"
                style={{ visibility: errorMessage ? "visible" : "hidden" }}
              >
                {errorMessage || "\u00A0"}
              </p>
              <Button
                id="login-submit-button"
                type="submit"
                variant="primary"
                size="lg"
                fullWidth={true}
              >
                Continue
              </Button>
            </div>
          </form>

          {registrationOpen && (
            <p className="text-center text-sm text-fg-muted">
              Don&apos;t have an account?{" "}
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate("/register")}
              >
                Sign up
              </Button>
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
